import React from 'react';
import { Button, Dialog, DialogContent, DialogFooter, DialogTrigger, Flex, Text } from '@chatsdet/ui';
import { CloudArrowUp, Paperclip } from '@phosphor-icons/react';

const ALLOWED_TYPES = new Set([
  'application/pdf',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/json',
]);

const BLOCKED_EXTENSIONS = ['.xlsm', '.xlsb', '.xltm', '.xlam', '.docm', '.dotm'];

const isAllowed = (file: File) => {
  // Block macro-enabled Excel files by extension
  const lowerName = file.name.toLowerCase();
  if (BLOCKED_EXTENSIONS.some((ext) => lowerName.endsWith(ext))) {
    return false;
  }

  // Allow by MIME type
  if (file.type.startsWith('image/') || ALLOWED_TYPES.has(file.type)) {
    return true;
  }

  // Allow by extension (fallback for when browser doesn't provide correct MIME type)
  if (
    lowerName.endsWith('.csv') ||
    lowerName.endsWith('.xlsx') ||
    lowerName.endsWith('.xls') ||
    lowerName.endsWith('.docx') ||
    lowerName.endsWith('.json')
  ) {
    return true;
  }

  return false;
};

type AttachmentUploadDialogProps = {
  isUploading?: boolean;
  onUpload: (files: File[]) => void;
  triggerLabel?: string;
  trigger?: React.ReactElement;
};

export function AttachmentUploadDialog({
  isUploading,
  onUpload,
  triggerLabel = 'Upload',
  trigger,
}: AttachmentUploadDialogProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const reset = React.useCallback(() => {
    setFiles([]);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const close = React.useCallback(() => {
    setIsOpen(false);
    reset();
  }, [reset]);

  const addFiles = React.useCallback((incoming: File[]) => {
    const allowed = incoming.filter(isAllowed);
    setFiles((prev) => {
      const next = [...prev];
      for (const file of allowed) {
        const duplicate = next.some(
          (existing) => existing.name === file.name && existing.size === file.size
        );
        if (!duplicate) {
          next.push(file);
        }
      }
      return next;
    });
  }, []);

  const handlePick = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(event.target.files ?? []);
      addFiles(picked);
    },
    [addFiles]
  );

  const handleDrop = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const dropped = Array.from(event.dataTransfer.files ?? []);
      addFiles(dropped);
    },
    [addFiles]
  );

  const handleUpload = React.useCallback(() => {
    if (files.length === 0) return;
    onUpload(files);
    close();
  }, [close, files, onUpload]);

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          reset();
        }
      }}
    >
      {trigger ?? (
        <Button variant="primary">
          <Flex align="center" gap="sm">
            <CloudArrowUp size={16} />
            {triggerLabel}
          </Flex>
        </Button>
      )}
      <Dialog title="Upload attachments">
        <DialogContent className="space-y-3">
          <div className="space-y-3">
            <div
              data-dropzone
              className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <Flex direction="column" align="center" gap="sm">
                <Paperclip size={20} className="text-muted-foreground" />
                <div>
                  <Text className="font-medium">Drop files here</Text>
                  <Text size="sm" className="text-muted-foreground">
                    Images, PDFs, CSV, Excel, Word, and JSON files.
                  </Text>
                </div>
                <Button
                  variant="primary"
                  onPress={() => inputRef.current?.click()}
                  isDisabled={isUploading}
                >
                  Choose files
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept="image/*,application/pdf,text/csv,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,application/vnd.ms-excel,.xls,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/json,.json"
                  className="hidden"
                  onChange={handlePick}
                />
              </Flex>
            </div>

            {files.length > 0 && (
              <div className="rounded-lg border border-border/60 bg-background p-3">
                <Text size="sm" className="font-medium mb-2">
                  Ready to upload ({files.length})
                </Text>
                <ul className="space-y-1">
                  {files.slice(0, 8).map((file) => (
                    <li
                      key={`${file.name}-${file.size}`}
                      className="text-xs text-muted-foreground truncate"
                    >
                      {file.name}
                    </li>
                  ))}
                  {files.length > 8 && (
                    <li className="text-xs text-muted-foreground">…and {files.length - 8} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="secondary" onPress={close} isDisabled={isUploading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleUpload}
            isDisabled={isUploading || files.length === 0}
          >
            Upload
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
